import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import { useProducts } from "../../hooks/useProducts";
import { EntityStatus } from "../../types";

export interface DeveloperGuideRelationOption {
  uuid: string;
  name: string;
  status?: string;
}

export interface DeveloperGuideRelationsSelection {
  apis: DeveloperGuideRelationOption[];
  products: DeveloperGuideRelationOption[];
}

interface DeveloperGuideRelationsPickerProps {
  value: DeveloperGuideRelationsSelection;
  onChange: (value: DeveloperGuideRelationsSelection) => void;
  disabled?: boolean;
}

const getUnselectedOptions = (
  all: DeveloperGuideRelationOption[],
  selected: DeveloperGuideRelationOption[],
) => {
  const selectedIds = new Set(selected.map((o) => o.uuid));
  return all.filter(
    (o) =>
      !selectedIds.has(o.uuid) &&
      (o.status == null || o.status === EntityStatus.ACTIVE),
  );
};

const renderGreenChips = (
  value: DeveloperGuideRelationOption[],
  getTagProps: (args: { index: number }) => Record<string, unknown>,
) =>
  value.map((option, index) => (
    <Chip
      {...getTagProps({ index })}
      key={option.uuid}
      label={option.name}
      sx={{ bgcolor: "#90e790", fontWeight: 600 }}
    />
  ));

const DeveloperGuideRelationsPicker: React.FC<
  DeveloperGuideRelationsPickerProps
> = ({ value, onChange, disabled = false }) => {
  const [apis, setApis] = useState<DeveloperGuideRelationOption[]>([]);
  const [loadingApis, setLoadingApis] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const { products, loading: loadingProducts } = useProducts();

  useEffect(() => {
    let cancelled = false;
    setLoadingApis(true);
    setApiError(null);
    apiService
      .getApis()
      .then((data) => {
        if (!cancelled) {
          setApis(
            data.map((a) => ({ uuid: a.uuid, name: a.name, status: a.status })),
          );
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setApiError(
            err instanceof Error ? err.message : "Failed to fetch APIs",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingApis(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const productOptions: DeveloperGuideRelationOption[] = products.map((p) => ({
    uuid: p.uuid,
    name: p.name,
    status: p.status,
  }));

  const patch = (partial: Partial<DeveloperGuideRelationsSelection>) =>
    onChange({ ...value, ...partial });

  return (
    <>
      <Autocomplete
        multiple
        disabled={disabled}
        options={getUnselectedOptions(apis, value.apis)}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
        value={value.apis}
        onChange={(_, v) => patch({ apis: v })}
        loading={loadingApis}
        renderTags={renderGreenChips}
        renderInput={(p) => (
          <TextField
            {...p}
            label="Linked APIs"
            fullWidth
            margin="normal"
            error={!!apiError}
            helperText={apiError}
            InputProps={{
              ...p.InputProps,
              endAdornment: (
                <>
                  {loadingApis && <CircularProgress size={18} />}
                  {p.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {!loadingProducts && (
        <Autocomplete
          multiple
          disabled={disabled}
          options={getUnselectedOptions(productOptions, value.products)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.uuid === b.uuid}
          value={value.products}
          onChange={(_, v) => patch({ products: v })}
          renderTags={renderGreenChips}
          renderInput={(p) => (
            <TextField {...p} label="Linked Products" fullWidth margin="normal" />
          )}
        />
      )}
    </>
  );
};

export default DeveloperGuideRelationsPicker;

export function guideToRelationsSelection(guide: {
  apis?: { uuid: string; name: string; status?: string }[];
  products?: { uuid: string; name: string; status?: string }[];
}): DeveloperGuideRelationsSelection {
  const map = (items?: { uuid: string; name: string; status?: string }[]) =>
    (items ?? []).map((item) => ({
      uuid: item.uuid,
      name: item.name,
      status: item.status,
    }));
  return {
    apis: map(guide.apis),
    products: map(guide.products),
  };
}

export function relationsSelectionToGuideUuids(
  selection: DeveloperGuideRelationsSelection,
) {
  return {
    apiUuids: selection.apis.map((a) => a.uuid),
    productUuids: selection.products.map((p) => p.uuid),
  };
}
